import { clamp } from './math';

export const DEFAULT_SAFETY_WEIGHTS = {
  toxicity: 0.45,
  hatespeech: 0.35,
  disinfo: 0.2
};

export const DEFAULT_SAFETY_SETTINGS = {
  gamma: 0.7,
  floor: 0.2
};

export const DEFAULT_REACH_SETTINGS = {
  alpha: 0.7,
  delta: 0.2,
  floor: 0.5,
  ceiling: 1,
  riskCeiling: 0.7
};

export function computeSafetyRisk(metrics = {}, weights = DEFAULT_SAFETY_WEIGHTS) {
  const toxicity = clamp(metrics.toxicity ?? 0, 0, 1);
  const hatespeech = clamp(metrics.hatespeech ?? 0, 0, 1);
  const disinfo = clamp(metrics.disinfo ?? 0, 0, 1);
  const totalWeight = (weights.toxicity ?? 0) + (weights.hatespeech ?? 0) + (weights.disinfo ?? 0);
  const norm = totalWeight > 0 ? totalWeight : 1;
  const score =
    toxicity * (weights.toxicity ?? 0) +
    hatespeech * (weights.hatespeech ?? 0) +
    disinfo * (weights.disinfo ?? 0);
  return clamp(score / norm, 0, 1);
}

export function computeSafetyAdjust(risk, settings = DEFAULT_SAFETY_SETTINGS) {
  const gamma = settings.gamma ?? DEFAULT_SAFETY_SETTINGS.gamma;
  const floor = settings.floor ?? DEFAULT_SAFETY_SETTINGS.floor;
  const adjust = 1 - gamma * clamp(risk, 0, 1);
  return clamp(adjust, floor, 1);
}

export function computeReachMultiplier({
  safetyRisk,
  earlyEngagement = 0,
  alpha = DEFAULT_REACH_SETTINGS.alpha,
  delta = DEFAULT_REACH_SETTINGS.delta,
  floor = DEFAULT_REACH_SETTINGS.floor,
  ceiling = DEFAULT_REACH_SETTINGS.ceiling,
  riskCeiling = DEFAULT_REACH_SETTINGS.riskCeiling
} = {}) {
  const risk = clamp(safetyRisk ?? 0, 0, 1);
  const engagement = clamp(earlyEngagement ?? 0, 0, 1);
  const engagementBoost = Math.sqrt(engagement);
  let multiplier = (1 - alpha * risk) + delta * engagementBoost;
  const maxAllowed = risk > 0.3 ? Math.min(ceiling, riskCeiling) : ceiling;
  multiplier = clamp(multiplier, floor, maxAllowed);
  return multiplier;
}

export function applyReachThrottling(baseReach, options = {}) {
  const multiplier = computeReachMultiplier(options);
  return {
    multiplier,
    reach: Math.round((baseReach ?? 0) * multiplier)
  };
}

const safetyEngine = {
  computeSafetyRisk,
  computeSafetyAdjust,
  computeReachMultiplier,
  applyReachThrottling
};

export default safetyEngine;

